import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  message,
  Popconfirm,
  Descriptions,
  Image,
  Statistic,
  Row,
  Col,
  Input,
  Select,
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../services/api';
import { API_CONFIG } from '../config';

const { Search } = Input;
const { Option } = Select;

// 获取完整图片URL
const getImageUrl = (url: string | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const baseUrl = API_CONFIG.baseURL.replace('/api/v1', '');
  return baseUrl + url;
};

interface VerificationRecord {
  id: number;
  userId: number;
  companyName: string;
  businessLicense: string;
  businessLicenseImage: string;
  legalPerson: string;
  legalPersonIdCard: string;
  legalPersonIdCardFront: string;
  legalPersonIdCardBack: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  address: string;
  businessScope: string;
  registeredCapital: string;
  establishedDate: string;
  electricityUsage: number;
  electricityBill: number;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  submitCount: number;
  submittedAt: string;
  reviewedAt: string;
  reviewedBy: number;
  reviewRemark: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    phone: string;
    nickname: string;
    avatarUrl: string;
  };
  reviewer?: {
    id: number;
    nickname: string;
    phone: string;
  };
}

interface VerificationStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const VerificationManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VerificationRecord[]>([]);
  const [stats, setStats] = useState<VerificationStats>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [keyword, setKeyword] = useState('');
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<VerificationRecord | null>(null);
  const [clearLoading, setClearLoading] = useState(false);

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/admin/enterprise-verification/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('获取统计数据失败', error);
    }
  };

  // 获取列表数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize };
      if (statusFilter) params.status = statusFilter;
      if (keyword) params.keyword = keyword;

      const response = await apiClient.get('/admin/enterprise-verification/list', { params });
      if (response.data.success) {
        setData(response.data.data.list);
        setTotal(response.data.data.total);
      }
    } catch (error) {
      console.error('获取数据失败', error);
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter]);

  // 审核操作
  const handleAudit = async (id: number, action: 'approve' | 'reject', remark?: string) => {
    try {
      const response = await apiClient.post(`/admin/enterprise-verification/${id}/audit`, {
        action,
        remark,
      });
      if (response.data.success) {
        message.success(action === 'approve' ? '审核通过' : '已拒绝');
        fetchData();
        fetchStats();
      }
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  // 清除所有测试数据
  const handleClearAll = async () => {
    setClearLoading(true);
    try {
      const response = await apiClient.delete('/admin/enterprise-verification/clear-all');
      if (response.data.success) {
        message.success('测试数据已清除');
        fetchData();
        fetchStats();
      }
    } catch (error: any) {
      message.error(error.message || '清除失败');
    } finally {
      setClearLoading(false);
    }
  };

  // 搜索
  const handleSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
    fetchData();
  };

  // 状态标签颜色
  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: '草稿' },
      pending: { color: 'processing', text: '待审核' },
      approved: { color: 'success', text: '已通过' },
      rejected: { color: 'error', text: '已拒绝' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 表格列定义
  const columns: ColumnsType<VerificationRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '企业名称',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 200,
      ellipsis: true,
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 100,
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 130,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: '提交次数',
      dataIndex: 'submitCount',
      key: 'submitCount',
      width: 90,
    },
    {
      title: '提交时间',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 170,
      render: (time) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setCurrentRecord(record);
              setDetailVisible(true);
            }}
          >
            详情
          </Button>
          {record.status === 'pending' && (
            <>
              <Popconfirm
                title="确认通过审核？"
                onConfirm={() => handleAudit(record.id, 'approve')}
                okText="确认"
                cancelText="取消"
              >
                <Button type="link" size="small" style={{ color: '#52c41a' }}>
                  通过
                </Button>
              </Popconfirm>
              <Popconfirm
                title="确认拒绝？"
                onConfirm={() => handleAudit(record.id, 'reject', '信息不符合要求')}
                okText="确认"
                cancelText="取消"
              >
                <Button type="link" size="small" danger>
                  拒绝
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总申请数" value={stats.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待审核"
              value={stats.pending}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已通过"
              value={stats.approved}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已拒绝"
              value={stats.rejected}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 主内容 */}
      <Card
        title="企业确权管理"
        extra={
          <Space>
            <Select
              placeholder="状态筛选"
              style={{ width: 120 }}
              allowClear
              value={statusFilter || undefined}
              onChange={(value) => {
                setStatusFilter(value || '');
                setPage(1);
              }}
            >
              <Option value="draft">草稿</Option>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
            <Search
              placeholder="搜索企业名称/联系人"
              onSearch={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
              刷新
            </Button>
            <Popconfirm
              title={
                <div>
                  <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  确定要清除所有确权测试数据吗？
                  <br />
                  <span style={{ color: '#ff4d4f' }}>此操作不可恢复！</span>
                </div>
              }
              onConfirm={handleClearAll}
              okText="确认清除"
              cancelText="取消"
              okButtonProps={{ danger: true, loading: clearLoading }}
            >
              <Button danger icon={<DeleteOutlined />} loading={clearLoading}>
                清除测试数据
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="确权申请详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {currentRecord && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="状态" span={2}>
              {getStatusTag(currentRecord.status)}
            </Descriptions.Item>
            <Descriptions.Item label="企业名称" span={2}>
              {currentRecord.companyName}
            </Descriptions.Item>
            <Descriptions.Item label="营业执照号">
              {currentRecord.businessLicense}
            </Descriptions.Item>
            <Descriptions.Item label="注册资本">
              {currentRecord.registeredCapital || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="营业执照照片" span={2}>
              {currentRecord.businessLicenseImage && (
                <Image src={getImageUrl(currentRecord.businessLicenseImage)} width={200} />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="法人代表">
              {currentRecord.legalPerson}
            </Descriptions.Item>
            <Descriptions.Item label="法人身份证号">
              {currentRecord.legalPersonIdCard}
            </Descriptions.Item>
            <Descriptions.Item label="身份证正面" span={2}>
              {currentRecord.legalPersonIdCardFront && (
                <Image src={getImageUrl(currentRecord.legalPersonIdCardFront)} width={200} />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="联系人">
              {currentRecord.contactPerson}
            </Descriptions.Item>
            <Descriptions.Item label="联系电话">
              {currentRecord.contactPhone}
            </Descriptions.Item>
            <Descriptions.Item label="联系邮箱">
              {currentRecord.contactEmail || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="企业地址" span={2}>
              {currentRecord.address}
            </Descriptions.Item>
            <Descriptions.Item label="经营范围" span={2}>
              {currentRecord.businessScope || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="提交时间">
              {currentRecord.submittedAt ? new Date(currentRecord.submittedAt).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="提交次数">
              {currentRecord.submitCount}
            </Descriptions.Item>
            {currentRecord.reviewRemark && (
              <Descriptions.Item label="审核备注" span={2}>
                {currentRecord.reviewRemark}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default VerificationManagement;